import React, { useCallback, useEffect } from "react"
import _ from "lodash"

import { pushDataLayer } from "utils/googletagmanager/pushDataLayer"
import { pluralize } from "utils/string"
import { GTM_AMENITY_SEARCH } from "constant"

import { useTheme, Box } from "@mui/material"
import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useMap } from "hooks/map/useMap"
import { useHighLightFeaturesMap } from "hooks/feature/useHighLightFeaturesMap"

import { AppNavigation } from "containers/kiosk-v/AppNavigation"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"

import { FooterAction } from "extensions/one-siam/components/domains/navigation"
import { OrdinalSelectSplide } from "extensions/one-siam/components/OrdinalSelector/OrdinalSelectSplide"

export const FeatureByCategoryKioskVPage = () => {
  const { isMultiOrdinalVenue, dataLoaded } = useVenue()
  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const {
    category,
    viewingOrdinal,
    viewingVenueId,
    levelInVenue,
    quickSearchFeatures,
    quickSearchCategories,
    searchParams,
    comingFromOrdinal,
    findNearestOrdinal,
    setSearchParams,
    toggleQuickSearch,
  } = useMap()

  const enableMap =
    !_.isNil(viewingOrdinal) && _.isNil(comingFromOrdinal) && dataLoaded

  const handleClickQuickSearch = useCallback(
    (featureType, category) => {
      const pluralType = pluralize(featureType)
      toggleQuickSearch(pluralType, category)
    },
    [toggleQuickSearch]
  )

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )

  useEffect(() => {
    // When ordinal navigate from different page
    if (viewingVenueId && dataLoaded && !_.isNil(comingFromOrdinal)) {
      const nearestOrdinal = findNearestOrdinal(comingFromOrdinal)
      setSearchParams(
        { venue: viewingVenueId, ordinal: nearestOrdinal },
        { replace: true }
      )
    } else if (viewingVenueId && dataLoaded && _.isNil(viewingOrdinal)) {
      // When ordinal is not provided
      const nearestOrdinal = findNearestOrdinal(0)
      setSearchParams(
        { venue: viewingVenueId, ordinal: nearestOrdinal },
        { replace: true }
      )
    }
  }, [
    dataLoaded,
    category,
    comingFromOrdinal,
    viewingVenueId,
    viewingOrdinal,
    findNearestOrdinal,
    setSearchParams,
  ])

  useHighLightFeaturesMap(quickSearchFeatures, Number(viewingOrdinal), {
    offset: { bottom: 800 },
    enabled: enableMap,
  })

  useEffect(() => {
    if (category) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: category,
      })
    }
  }, [category])

  const theme = useTheme()
  const {
    actionContainer,
    actionButton,
    searchBarContainer,
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    navigationWrapper,
    ordinalSelectMenu,
    mainMenuContainer,
    footerActionContainer,
  } = theme.components[`AppMap`]?.styleOverrides || {}

  return (
    <>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelectSplide
            selectedOrdinal={Number(viewingOrdinal)}
            levels={levelInVenue}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}

      <Box key="app-navigation" sx={navigationWrapper}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
        <Box sx={toolbarContainer}>
          <Box sx={actionContainer}>
            <LanguageButton variant="contained" sx={actionButton} />
          </Box>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search shops and more...")}
              onClick={openSearch}
            />
          </Box>
        </Box>
        <Box sx={mainMenuContainer}>
          <AppNavigation />
          <FooterAction sx={footerActionContainer} />
        </Box>
      </Box>
    </>
  )
}
